import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

type ThemeMode = 'dark' | 'light';

interface ThemeModeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
  isDark: boolean;
}

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: 'dark', toggleMode: () => {}, setMode: () => {}, isDark: true,
});

const STORAGE_KEY = 'bisolution_theme_mode';

function getInitialMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {}
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);
  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    try { localStorage.setItem(STORAGE_KEY, newMode); } catch {}
  }, []);
  const toggleMode = useCallback(() => { setMode(mode === 'dark' ? 'light' : 'dark'); }, [mode, setMode]);
  const value = useMemo(() => ({ mode, toggleMode, setMode, isDark: mode === 'dark' }), [mode, toggleMode, setMode]);
  return React.createElement(ThemeModeContext.Provider, { value }, children);
};

export const useThemeMode = () => useContext(ThemeModeContext);
